import {Box, Card, Container, Flex, Group, Menu, rem, Text, Title, UnstyledButton} from "@mantine/core";
import {useMemo, useState} from "react";
import {customFieldProfilesSelector, useGetAllCustomFieldProfilesQuery} from "@ots-frontend/ots-data";
import {skipToken} from "@reduxjs/toolkit/query";
import {IconChevronDown} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {ResizeableContent} from "../../components/ResizableContent/ResizableContent";
import {PAGINATION_PAGE_SIZES} from "../../components/data-table/DataTable/usePagination";
import {
  CustomFieldProfileDataTable
} from "../../components/data-table/CustomFieldProfileDataTable/CustomFieldProfileDataTable";
import {RouteBreadcrumbs} from "../../components/breadcrumb/RouteBreadcrumbs/RouteBreadcrumbs";
import {ProtectedPage} from "../../components/page/ProtectedPage/ProtectedPage";
import {useNavigate} from "react-router-dom";
import {HasAccess} from "../../components/auth/HasAccess/HasAccess";

export type CustomFieldProfilePageProps = {}

export const CustomFieldProfileDataTablePageRoute = () => {
  return (
      <CustomFieldProfileDataTablePage/>
  );
};

export const CustomFieldProfileDataTablePage = (props: CustomFieldProfilePageProps) => {

  const {t} = useTranslation(["translation", "common"]);
  const navigate = useNavigate()

  const [access, setAccess] = useState(false);

  const selectCustomFieldProfiles = useMemo(() => {
    return customFieldProfilesSelector();
  }, []);

  const {
    customFieldProfiles,
    isLoading,
    error,
    refetch,
  } = useGetAllCustomFieldProfilesQuery(access ? undefined : skipToken, {
    selectFromResult: (result) => ({
      ...result,
      customFieldProfiles: selectCustomFieldProfiles(result),
      isLoading: result.isLoading,
      error: result.error,
    })
  });

  return (
      <ProtectedPage
          permissions={["customfieldprofile_view"]}
          isLoading={isLoading}
          error={error}
          onAccess={setAccess}
      >
        {(classes) => (
            <ResizeableContent>
              {(size) => (
                  <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="md">
                    <Flex direction="column" h="100%" m="sm" gap="sm">
                      <RouteBreadcrumbs/>
                      <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                        <Group position="apart">
                          <Title order={4}>
                            {t("common.entity.customfieldprofile.plural", "Custom Field Profiles", {ns: "common"})}
                          </Title>
                          <Menu
                              width={260}
                              position="bottom-end"
                              transitionProps={{transition: "pop-top-right"}}
                              withinPortal
                          >
                            <Menu.Target>
                              <UnstyledButton>
                                <Group spacing={7}>
                                  <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                                    {t("common.actions.plural", "Actions", {ns: "common"})}
                                  </Text>
                                  <IconChevronDown size={rem(12)} stroke={1.5}/>
                                </Group>
                              </UnstyledButton>
                            </Menu.Target>
                            <Menu.Dropdown>
                              <Menu.Item onClick={() => refetch()}>
                                {t("common.actions.refresh", "Refresh", {ns: "common"})}
                              </Menu.Item>
                              <HasAccess permissions={["customfieldprofile_create"]}>
                                <>
                                  <Menu.Divider/>
                                  <Menu.Item onClick={() => navigate("/custom-field-profiles/form")}>
                                    {t("common.action.new", "New", {ns: "common"})}
                                  </Menu.Item>
                                </>
                              </HasAccess>
                            </Menu.Dropdown>
                          </Menu>
                        </Group>
                      </Card>
                      <Box sx={{flex: 1, overflow: 'auto'}}>
                        <CustomFieldProfileDataTable
                            withBorder
                            records={customFieldProfiles}
                            recordsPerPageOptions={PAGINATION_PAGE_SIZES}
                            fetching={isLoading}/>
                      </Box>
                    </Flex>
                  </Container>
              )}
            </ResizeableContent>
        )}
      </ProtectedPage>
  );
};
